import { motion } from "framer-motion";

/* Progressive color palettes per rating level (index = level - 1).
   Level 1 monochrome → Level 5 premium full color with warm lighting. */
const C = {
  wall: ["#8f8f8f", "#D9CBB4", "#E5D3B8", "#EBD9B4", "#F0DFC0"],
  wall2: ["#6f6f6f", "#B5A48C", "#C4AF92", "#CBB293", "#D6BC9C"],
  roof: ["#4a4a4a", "#6E5B4C", "#7A5B43", "#8A5A3B", "#96603A"],
  trim: ["#5a5a5a", "#8a7a68", "#B08D57", "#C9A227", "#D4AF37"],
  glass: ["#bdbdbd", "#9aa5ad", "#7fa3b8", "#F2C979", "#FFD98A"],
  door: ["#3f3f3f", "#5c4a3a", "#6B4A34", "#7A4A2E", "#8A4B28"],
  green: ["#7a7a7a", "#7a7a7a", "#5B8C5A", "#4E8A55", "#3E9B5F"],
  green2: ["#6e6e6e", "#6e6e6e", "#6B9C68", "#5E9A60", "#4FAE6E"],
  trunk: ["#555555", "#555555", "#6B4A34", "#7A4A2E", "#8A4B28"],
  path: ["#666666", "#666666", "#9a8f80", "#B5A488", "#C9B18C"],
  ground: ["#3a3a3a", "#4a4038", "#4f4438", "#5A4A38", "#6B5638"],
};

export const HouseArt = ({ level }) => {
  const lv = Math.min(5, Math.max(1, level || 1));
  const i = lv - 1;
  const on = (min) => (lv >= min ? 1 : 0);

  return (
    <svg
      viewBox="0 0 320 240"
      className="house-art w-full h-full"
      role="img"
      aria-label={`Modern house illustration, finish level ${lv} of 5`}
    >
      {/* warm backdrop glow (level 4+) */}
      <circle cx="160" cy="115" r="98" style={{ fill: "#D4AF37", opacity: lv >= 4 ? (lv === 5 ? 0.16 : 0.08) : 0 }} />

      {/* ground */}
      <rect x="30" y="196" width="260" height="5" rx="2.5" style={{ fill: C.ground[i] }} />

      {/* garden path (level 3+) */}
      <polygon points="238,196 262,196 276,222 224,222" style={{ fill: C.path[i], opacity: on(3) }} />

      {/* tree (level 3+) */}
      <g style={{ opacity: on(3) }}>
        <rect x="44" y="158" width="6" height="38" style={{ fill: C.trunk[i] }} />
        <circle cx="47" cy="146" r="20" style={{ fill: C.green2[i] }} />
        <circle cx="36" cy="156" r="12" style={{ fill: C.green[i] }} />
      </g>

      {/* main volume + flat roof slab */}
      <rect x="80" y="100" width="140" height="96" style={{ fill: C.wall[i] }} />
      <rect x="65" y="88" width="170" height="14" rx="3" style={{ fill: C.roof[i] }} />

      {/* side volume */}
      <rect x="220" y="126" width="60" height="70" style={{ fill: C.wall2[i] }} />
      <rect x="214" y="116" width="72" height="11" rx="3" style={{ fill: C.roof[i] }} />

      {/* architectural trim line (level 3+) */}
      <rect x="80" y="106" width="140" height="3" style={{ fill: C.trim[i], opacity: on(3) }} />
      <rect x="80" y="100" width="3" height="96" style={{ fill: C.trim[i], opacity: on(3) }} />

      {/* windows with mullions */}
      <rect x="96" y="122" width="52" height="42" rx="2" style={{ fill: C.glass[i] }} />
      <rect x="121" y="122" width="2.5" height="42" style={{ fill: C.wall[i] }} />
      <rect x="96" y="141" width="52" height="2.5" style={{ fill: C.wall[i] }} />
      <rect x="160" y="122" width="42" height="42" rx="2" style={{ fill: C.glass[i] }} />
      <rect x="180" y="122" width="2.5" height="42" style={{ fill: C.wall[i] }} />

      {/* door + handle */}
      <rect x="238" y="146" width="24" height="50" rx="2" style={{ fill: C.door[i] }} />
      <circle cx="257" cy="172" r="1.8" style={{ fill: C.trim[i] }} />

      {/* porch light (level 4+) */}
      <circle cx="250" cy="136" r="4" style={{ fill: "#FFD98A", opacity: on(4) }} />
      <circle cx="250" cy="136" r="9" style={{ fill: "#FFD98A", opacity: lv >= 4 ? (lv === 5 ? 0.35 : 0.18) : 0 }} />

      {/* landscaping bushes (level 3+) */}
      <g style={{ opacity: on(3) }}>
        <circle cx="86" cy="190" r="12" style={{ fill: C.green[i] }} />
        <circle cx="206" cy="192" r="9" style={{ fill: C.green[i] }} />
        <circle cx="294" cy="190" r="10" style={{ fill: C.green[i] }} />
      </g>

      {/* warm string lights along the roof edge (level 5 only) */}
      <g style={{ opacity: lv === 5 ? 1 : 0 }}>
        {[82, 107, 132, 157, 182, 207, 228].map((x, k) => (
          <circle key={k} cx={x} cy={106 + (k % 2) * 3} r="2.2" style={{ fill: "#F5D67B" }} />
        ))}
      </g>
    </svg>
  );
};

export const HouseRating = ({ value, onChange, descriptions, legend, pickHint, testIdPrefix = "house-rating" }) => {
  const artLevel = value >= 1 ? value : 1;

  return (
    <div data-testid={`${testIdPrefix}-widget`}>
      <motion.div
        key={artLevel}
        initial={{ opacity: 0.55, scale: 0.97 }}
        animate={{ opacity: value >= 1 ? 1 : 0.55, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-sm mx-auto"
      >
        <HouseArt level={artLevel} />
      </motion.div>

      <div role="radiogroup" aria-label={legend} className="mt-5 flex justify-center gap-3">
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${n} / 5 — ${descriptions[n - 1]}`}
              onClick={() => onChange(n)}
              data-testid={`${testIdPrefix}-${n}`}
              className={`h-11 w-11 border text-sm font-semibold transition-all duration-300 ${
                selected
                  ? "bg-[#D4AF37] text-[#1A1412] border-[#D4AF37] scale-110 shadow-[0_6px_20px_rgba(201,162,39,0.35)]"
                  : "border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-center text-sm text-[#B8A99A]" aria-live="polite" data-testid={`${testIdPrefix}-description`}>
        {value >= 1 ? `${value}/5 — ${descriptions[value - 1]}` : pickHint}
      </p>
    </div>
  );
};

export const HouseLevelBadge = ({ value }) => (
  <div className="flex items-center gap-2" data-testid="house-level-badge" aria-label={`Rated ${value} out of 5`}>
    <div className="h-11 w-14 shrink-0">
      <HouseArt level={value} />
    </div>
    <span className="text-xs text-[#B8A99A] tracking-wider">{value}/5</span>
  </div>
);
